import {useCallback, useLayoutEffect, useRef, type RefObject} from 'react';

const measurementProperties = [
  'font-family', 'font-size', 'font-style', 'font-weight', 'font-stretch',
  'font-variant', 'font-feature-settings', 'font-variation-settings', 'font-kerning',
  'line-height', 'letter-spacing', 'word-spacing', 'text-indent', 'text-transform',
  'text-rendering', 'text-size-adjust', '-webkit-text-size-adjust', 'tab-size',
  'white-space', 'overflow-wrap', 'word-break', 'direction', 'writing-mode',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
] as const;

/** Schedule sizing without measuring or temporarily collapsing the live input. */
export function useComposerSize(
  textarea: RefObject<HTMLTextAreaElement | null>,
  sizing: 'native-or-measured' | 'measured' = 'native-or-measured',
): () => void {
  const enqueue = useRef<() => void>(() => {});
  const schedule = useCallback(() => enqueue.current(), []);

  useLayoutEffect(() => {
    const input = textarea.current;
    if (!input) return;
    // The stylesheet supplies field-sizing: content, min-height and max-height.
    // Native sizing also keeps selection, composition and caret scrolling native.
    if (sizing === 'native-or-measured' && typeof CSS !== 'undefined' && CSS.supports('field-sizing', 'content')) return;

    const mirror = document.createElement('textarea');
    mirror.setAttribute('aria-hidden', 'true');
    mirror.tabIndex = -1;
    mirror.readOnly = true;
    mirror.spellcheck = false;
    mirror.rows = 1;
    mirror.style.cssText = 'all:initial;position:fixed;left:-100000px;top:0;display:block;visibility:hidden;pointer-events:none;contain:layout style paint;box-sizing:content-box;width:0;height:0;min-height:0;max-height:none;min-width:0;max-width:none;margin:0;border:0;overflow:hidden;resize:none;';
    document.body.append(mirror);

    let frame = 0;
    let disposed = false;
    let refreshStyle = true;
    let contentWidth = 0;
    let observedWidth: number | undefined;
    let verticalPadding = 0;
    let verticalBorder = 0;
    let borderBox = false;
    let minimum = 0;
    let maximum = Infinity;
    let appliedHeight: number | undefined;

    function updateStyle() {
      const style = getComputedStyle(input!);
      for (const property of measurementProperties) mirror.style.setProperty(property, style.getPropertyValue(property));
      const number = (value: string) => Number.parseFloat(value) || 0;
      const horizontalPadding = number(style.paddingLeft) + number(style.paddingRight);
      const horizontalBorder = number(style.borderLeftWidth) + number(style.borderRightWidth);
      verticalPadding = number(style.paddingTop) + number(style.paddingBottom);
      verticalBorder = number(style.borderTopWidth) + number(style.borderBottomWidth);
      borderBox = style.boxSizing === 'border-box';
      minimum = number(style.minHeight);
      maximum = Number.parseFloat(style.maxHeight);
      if (!Number.isFinite(maximum)) maximum = Infinity;
      // ResizeObserver supplies the untransformed content width, including after
      // a hidden Terminal view becomes visible or a scrollbar changes wrapping.
      contentWidth = observedWidth ?? Math.max(0, number(style.width) - (borderBox ? horizontalPadding + horizontalBorder : 0));
      mirror.style.width = `${contentWidth}px`;
      mirror.wrap = input!.wrap;
      appliedHeight = Number.parseFloat(input!.style.height) || undefined;
      refreshStyle = false;
    }

    function measure() {
      frame = 0;
      if (disposed) return;
      if (refreshStyle) updateStyle();
      if (contentWidth <= 0) return;
      mirror.value = input!.value;
      mirror.placeholder = input!.placeholder;
      const height = Math.max(minimum, Math.min(maximum,
        mirror.scrollHeight + (borderBox ? verticalBorder : -verticalPadding)));
      if (height === appliedHeight) return;
      appliedHeight = height;
      input!.style.height = `${height}px`;
    }

    function queue() {
      if (!frame && !disposed) frame = requestAnimationFrame(measure);
    }
    function invalidateStyle() {
      refreshStyle = true;
      queue();
    }
    enqueue.current = queue;

    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width === undefined || width === observedWidth) return;
      observedWidth = width;
      invalidateStyle();
    });
    observer.observe(input);
    window.addEventListener('resize', invalidateStyle);
    const fonts = document.fonts;
    fonts?.addEventListener('loadingdone', invalidateStyle);
    void fonts?.ready.then(() => {if (!disposed) invalidateStyle();});
    measure();

    return () => {
      disposed = true;
      enqueue.current = () => {};
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', invalidateStyle);
      fonts?.removeEventListener('loadingdone', invalidateStyle);
      mirror.value = '';
      mirror.remove();
    };
  }, [textarea, sizing]);

  return schedule;
}
